import { useState } from 'react';

import formatCnpj from '@brazilian-utils/format-cnpj';
import axios from 'axios';
import { PasswordValidator } from 'components/layout/password-validator/password-validator';
import PhoneInput from 'components/signup-input/phone-input';
import { CustomLabel } from 'components/signup-input/phone-input.style';
import { AsYouType } from 'libphonenumber-js';
import { TextLink } from 'pages/leads/components/shared/TextLink';
import { API_URLS } from 'pages/leads/constants';
import { shape, string } from 'prop-types';
import { Field, Form } from 'react-final-form';
import { Trans, useTranslation } from 'react-i18next';
import { customMutators } from 'utils/old-ui-commons/form';
import {
  BR_PHONE_PATTERN,
  MX_PHONE_PATTERN,
  US_PHONE_PATTERN,
  defaultInitialPhoneValues,
  getPhoneInformationObject,
} from 'utils/phone-patterns';

import { Alert, Box, Button, Card, FieldError, Flex, Label } from '@olist/united';

import { CheckboxWith, IncorrectCin, InputWith, PasswordWith } from './invite-signup-components';
import formValidator from './validator';

const labelErrors = (errors) => <FieldError messages={errors} />;

const formatPhoneToInput = (phone) => {
  if (!phone || phone.length !== 14) return '';
  const [, areaCode, mainPart, lastPart] = phone
    .replace('+55', '')
    .match(/^(\d{2})(\d{5})(\d{4})$/);
  return `(${areaCode}) ${mainPart}-${lastPart}`;
};

const SignupForm = ({ invite }) => {
  const { t } = useTranslation();

  const filteredInviteValues = {
    cnpj: invite.document_number ?? invite.cnpj ?? '',
    email: invite.email ?? '',
    name: invite.name ?? '',
    mobile_phone: formatPhoneToInput(invite.phone) ?? '',
  };
  const [phoneCountry, setPhoneCountry] = useState(defaultInitialPhoneValues);
  const [submitError, setSubmitError] = useState();
  const [submitCnpjError, setSubmitCnpjError] = useState(false);
  const [isPasswordValid, setPasswordValid] = useState(false);

  const handleChangePhoneCountry = (phoneParams) => {
    new AsYouType().reset();
    setPhoneCountry(phoneParams);
  };

  const handleDropdownItemsOptions = (value, prefixNumber, code, placeHolder, mask, form) => ({
    value,
    onClick: () => {
      form.mutators.setValue('mobile_phone', '');
      handleChangePhoneCountry({
        prefixNumber,
        code,
        mask,
        placeHolder,
      });
    },
  });

  const handleOnChangePhoneNumber = (args, state, utils) => {
    const input = args[0]?.target?.value;
    const phone = new AsYouType(phoneCountry.code).input(input);
    utils.changeValue(state, 'mobile_phone', () => phone);
  };

  const formSubmit = async (values) => {
    const phone = getPhoneInformationObject(values.mobile_phone, phoneCountry);
    const cleanValues = (formValues) => ({
      ...formValues,
      mobile_phone: phone.getNumber()?.number,
      cnpj: formValues.cnpj.replace(/[^0-9]/g, ''),
    });
    try {
      const formatValues = cleanValues(values);

      // eslint-disable-next-line @typescript-eslint/naming-convention
      const { cnpj, email, mobile_phone, name, password } = formatValues;
      const { id: leadId } = invite;
      const responseAccountCreated = await axios.post(
        API_URLS.postInvitesAccountCreation({ leadId }),
        {
          password,
          mobile_phone,
          cnpj,
          name,
          email,
        }
      );

      window.location.assign(
        responseAccountCreated?.data?.redirectTo || '/onboarding/first-steps/'
      );
    } catch ({ response }) {
      const shouldShowServerError = !response || !response?.data || response?.status === 500;
      const shouldShowCnpjError = response?.status === 400 && !!response?.data?.messages?.cnpj;
      setSubmitCnpjError(shouldShowCnpjError);
      const shouldShowConflictError = response?.status === 409;
      if (shouldShowConflictError) {
        return setSubmitError(t`This informations is used by other users`);
      }
      if (shouldShowServerError) {
        return setSubmitError(t`Something went wrong. Please try again or contact the support.`);
      }
      setSubmitError(response?.data?.message);
    }

    return null;
  };

  return (
    <Flex justifyContent="center">
      <Card mb={9} maxWidth="628px" width="100%" padding={['0px', '15px 43px', '40px 82px']}>
        <Form
          onSubmit={formSubmit}
          initialValues={filteredInviteValues}
          validate={formValidator}
          mutators={{
            setValue: customMutators.setValue,
            handleOnChangePhoneNumber,
          }}
          render={({
            dirtySinceLastSubmit,
            form,
            handleSubmit,
            hasSubmitErrors,
            hasValidationErrors,
            submitting,
          }) => (
            <form onSubmit={handleSubmit}>
              {submitError && (
                <Flex width={1} mb={5}>
                  <Alert messages={[submitError]} />
                </Flex>
              )}
              {submitCnpjError && (
                <Alert>
                  <IncorrectCin />
                </Alert>
              )}
              <Box mb={2}>
                <Label htmlFor="cnpj">cnpj</Label>
                <Field
                  component={InputWith}
                  format={formatCnpj}
                  id="cnpj"
                  name="cnpj"
                  placeholder="00.000.000/000-00"
                  required
                />
              </Box>
              <Box mb={2}>
                <Label htmlFor="name">{t`full name of the person in charge`}</Label>
                <Field
                  component={InputWith}
                  id="name"
                  name="name"
                  placeholder={t`e.g. Italy Alan`}
                  required
                />
              </Box>
              <Box mb={2}>
                <CustomLabel htmlFor="mobile_phone">{t`mobile phone`}</CustomLabel>
                <PhoneInput
                  id="mobile_phone"
                  name="mobile_phone"
                  defaultValue={phoneCountry.prefixNumber}
                  items={[
                    handleDropdownItemsOptions(
                      '+55 Brasil',
                      '+55',
                      'BR',
                      '(99) 99999-9999',
                      BR_PHONE_PATTERN,
                      form
                    ),
                    handleDropdownItemsOptions(
                      '+52 Mexico',
                      '+52',
                      'MX',
                      '999 999 9999',
                      MX_PHONE_PATTERN,
                      form
                    ),
                    handleDropdownItemsOptions(
                      '+1 Usa',
                      '+1',
                      'US',
                      '(999) 999-9999',
                      US_PHONE_PATTERN,
                      form
                    ),
                  ]}
                  handleOnChangeInput={form.mutators.handleOnChangePhoneNumber}
                  phoneValue={phoneCountry}
                  labelErrors={labelErrors}
                />
              </Box>
              <Box mb={2}>
                <Label htmlFor="email">{t`business email`}</Label>
                <Field id="email" name="email" component={InputWith} type="email" required />
              </Box>
              <Box mb={2}>
                <Label htmlFor="password">{t`create password`}</Label>
                <Field name="password" id="password" component={PasswordWith} required />

                <Box mt={3} mb={3}>
                  <PasswordValidator
                    onValid={(value) => setPasswordValid(value)}
                    password={form.getState().values?.password}
                  />
                </Box>
              </Box>
              <Box mb={2}>
                <Field
                  name="agreeTerms"
                  type="checkbox"
                  render={({ input, meta }) => (
                    <CheckboxWith
                      input={input}
                      meta={meta}
                      required
                      {...input}
                      label={
                        <Trans i18nKey="I read and agree with <3>olist</3> <1>use terms</1>">
                          I read and agree with <strong>olist</strong>{' '}
                          <TextLink
                            target="_blank"
                            href="https://olist.com/pt-br/seguranca-e-privacidade/termos-de-uso/"
                          >
                            use terms
                          </TextLink>
                        </Trans>
                      }
                    />
                  )}
                />
              </Box>
              <Flex mt={4} justifyContent="center">
                <Button
                  disabled={
                    (hasValidationErrors || (hasSubmitErrors && !dirtySinceLastSubmit)) &&
                    !isPasswordValid
                  }
                  loading={submitting}
                  type="submit"
                  fullWidth
                  variation="primary"
                >
                  {t`create account`}
                </Button>
              </Flex>
            </form>
          )}
        />
      </Card>
    </Flex>
  );
};

SignupForm.defaultProps = {
  invite: {},
};

SignupForm.propTypes = {
  invite: shape({
    id: string,
    cnpj: string,
    email: string,
    name: string,
  }),
};

export default SignupForm;
